class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));
    this.addEventListener('click', this.handleSuggestionClick.bind(this));

    this.isHeaderSearch = this.closest('header') !== null;
    /** Whether `.toggle-navigation-button` had `is-show` before header search opened (desktop collapse nav). */
    this._savedNavToggleShow = null;

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
      this.restoreNavToggleAfterSearch();
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };
    this.searchCloseBtn = document.querySelector('.header__search-close');
  
    this.searchCloseBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.closeSearch();
    });
  }
  closeSearch() {
    this.classList.remove(this.states.OPEN);
    document.body.classList.remove(this.states.SEARCH_OPEN);

    this.restoreNavToggleAfterSearch();

    if (this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }

    this.removeAttribute('results');
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  onFocus(event) {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );
    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
      this.rememberNavToggleAndHideForSearch();
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }
    this.classList.add('predictive-search-open');
    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent.classList.add('hidden');
      }
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url, event);
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.toggleSearchState(false);
  }

  handleProductTypeChange(evt) {
    const query = this.getQuery();
    if (query.length > 0) {
      const url = this.setupURL().toString();
      this.renderSection(url);
    }
  }

  setupURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    let search_term = this.getQuery();
    if (this.searchProductTypes && this.searchProductTypes.value != '') {
      search_term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(search_term)}`;
    }
    return (
      url.searchParams.set('q', search_term),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.setLoadingState(true);

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => this.setLoadingState(false));
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;
        this.validateSuggestions();

        if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
          document.body.classList.add('search-open');
          this.rememberNavToggleAndHideForSearch();
          this.classList.add('predictive-search-open');
        } else {
          this.classList.add('predictive-search-open');
        }
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    const target = event.target;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
          target.classList.contains('header__search-close'))
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      if (isOpen) {
        this.rememberNavToggleAndHideForSearch();
      } else {
        this.restoreNavToggleAfterSearch();
      }
    }
    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  rememberNavToggleAndHideForSearch() {
    if (!this.toggleNavigationButton) return;
    if (this._savedNavToggleShow === null) {
      this._savedNavToggleShow = this.toggleNavigationButton.classList.contains('is-show');
    }
    this.toggleNavigationButton.classList.remove('is-show');
  }

  restoreNavToggleAfterSearch() {
    if (!this.toggleNavigationButton) return;
    if (this._savedNavToggleShow !== null) {
      this.toggleNavigationButton.classList.toggle('is-show', this._savedNavToggleShow);
    }
    this._savedNavToggleShow = null;
  }

  async validateSuggestions() {
    const suggestionsList = this.querySelector('#predictive-search-results-queries-list');
    if (!suggestionsList) return;

    const items = [...suggestionsList.querySelectorAll('li')];
    if (items.length === 0) return;

    await Promise.all(
      items.map(async (li) => {
        const link = li.querySelector('a');
        const suggestionText = link?.getAttribute('aria-label');
        if (!suggestionText) return;

        try {
          const url = new URL('/search/suggest.json', window.location.origin);
          url.searchParams.set('q', suggestionText);
          url.searchParams.set('resources[type]', 'product');
          url.searchParams.set('resources[limit]', '1');

          const response = await fetch(url);
          if (!response.ok) return;

          const data = await response.json();
          const products = data?.resources?.results?.products ?? [];
          if (products.length === 0) li.remove();
        } catch {
          // keep suggestion on network error
        }
      })
    );

    if (suggestionsList.children.length === 0) {
      suggestionsList.closest('.predictive-search-result--queries')?.remove();
    }
  }

  handleSuggestionClick(event) {
    const suggestionLink = event.target.closest('#predictive-search-results-queries-list a');
    if (!suggestionLink) return;

    event.preventDefault();
    const suggestionText = suggestionLink.getAttribute('aria-label');
    if (suggestionText) {
      this.input.value = suggestionText;
      this.querySelector('form').submit();
    }
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);
